import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

/** Κατάσταση επιστροφής από λίστα → σελίδα λεπτομέρειας / φόρμα. */
export type ListReturnState = {
  listSearch?: string
  /** Πλήρης διαδρομή επιστροφής (pathname + search) όταν ανοίγει φόρμα από άλλη σελίδα. */
  returnTo?: string
}

export function useListReturnTo(basePath: string): string {
  const location = useLocation()
  const state = location.state as ListReturnState | null
  if (state?.returnTo) return state.returnTo
  if (state?.listSearch) return `${basePath}${state.listSearch}`
  return basePath
}

export function useListLinkState(): ListReturnState {
  const location = useLocation()
  return useMemo(
    () => ({
      listSearch: location.search || undefined,
      returnTo: `${location.pathname}${location.search}`,
    }),
    [location.pathname, location.search],
  )
}

/** Ίδιο με `useListLinkState` — κατάσταση για links προς φόρμες (επιστροφή στην τρέχουσα σελίδα). */
export const useLinkReturnState = useListLinkState

/** Επιστροφή στην προηγούμενη σελίδα (ή fallback) — για κλείσιμο φορμών. */
export function useNavigateBack(fallbackPath: string) {
  const navigate = useNavigate()
  const location = useLocation()

  return useCallback(() => {
    const state = location.state as ListReturnState | null
    if (state?.returnTo) {
      navigate(state.returnTo)
      return
    }
    if (location.key !== 'default') {
      navigate(-1)
      return
    }
    if (state?.listSearch) {
      navigate(`${fallbackPath}${state.listSearch}`)
      return
    }
    navigate(fallbackPath)
  }, [navigate, location, fallbackPath])
}

/** Πεδίο κειμένου με debounce στο URL (π.χ. αναζήτηση). */
export function useUrlDebouncedParam(key: string, debounceMs = 400) {
  const [params, setParams] = useSearchParams()
  const fromUrl = params.get(key) ?? ''
  const [draft, setDraft] = useState(fromUrl)

  useEffect(() => {
    setDraft(fromUrl)
  }, [fromUrl])

  useEffect(() => {
    const id = window.setTimeout(() => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          const v = draft.trim()
          if (v) next.set(key, v)
          else next.delete(key)
          return next
        },
        { replace: true },
      )
    }, debounceMs)
    return () => window.clearTimeout(id)
  }, [draft, key, debounceMs, setParams])

  return [draft, setDraft] as const
}

export function useUrlParam(key: string, defaultValue = '') {
  const [params, setParams] = useSearchParams()
  const value = params.get(key) ?? defaultValue
  const setValue = useCallback(
    (v: string) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          const trimmed = v.trim()
          if (trimmed) next.set(key, trimmed)
          else next.delete(key)
          return next
        },
        { replace: true },
      )
    },
    [key, setParams],
  )
  return [value, setValue] as const
}

export function useUrlEnumParam<T extends string>(
  key: string,
  allowed: readonly T[],
  defaultValue: T,
) {
  const [params, setParams] = useSearchParams()
  const value = useMemo((): T => {
    const raw = params.get(key)
    return raw && (allowed as readonly string[]).includes(raw)
      ? (raw as T)
      : defaultValue
  }, [params, key, allowed, defaultValue])

  const setValue = useCallback(
    (v: T) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (v === defaultValue) next.delete(key)
          else next.set(key, v)
          return next
        },
        { replace: true },
      )
    },
    [key, defaultValue, setParams],
  )

  return [value, setValue] as const
}

export function useUrlParamList(key: string) {
  const [params, setParams] = useSearchParams()
  const list = useMemo(() => {
    const raw = params.get(key)
    return raw ? raw.split(',').filter(Boolean) : []
  }, [params, key])

  const setList = useCallback(
    (items: string[]) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (items.length > 0) next.set(key, items.join(','))
          else next.delete(key)
          return next
        },
        { replace: true },
      )
    },
    [key, setParams],
  )

  const toggle = useCallback(
    (item: string) => {
      setList(
        list.includes(item)
          ? list.filter((x) => x !== item)
          : [...list, item],
      )
    },
    [list, setList],
  )

  const clear = useCallback(() => setList([]), [setList])

  return { list, setList, toggle, clear }
}

export function useClearUrlParams() {
  const [, setParams] = useSearchParams()
  return useCallback(() => {
    setParams(new URLSearchParams(), { replace: true })
  }, [setParams])
}
